import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner, Alert } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import api from '../../services/api';
import { exportExcel } from '../decisionnel/utils/exports';
import { fmtDateFr } from './format';
import { FicheLien } from '../dashboard/components/commun';

// Fenêtre de détail d'un chiffre du Point du jour.
// detail = { titre, kind, ids:Set|null, comId?, live? }
//   kind « figé » : lignes prises dans data.details (figées avec le rapport enregistré)
//   live = { ind, periode, row } : lignes chargées depuis /decisionnel/objectif-commercial/detail

const nf = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });
const mt = (v) => nf.format(Number(v || 0));
const dt = (v) => (v ? fmtDateFr(String(v).slice(0, 10)) : '');

// Fiche du contrat dans l'application Shems
const CONSULTATION_URL = 'https://shems.ngrok.io/consultation/';

// N° de dossier cliquable (un encaissement peut couvrir plusieurs dossiers)
const Dossiers = ({ r }) => {
  const list = r.dossiers?.length ? r.dossiers : [];
  if (!list.length) return r.numDoss || '';
  return list.map((d, i) => (
    <React.Fragment key={d.id}>
      {i > 0 && ', '}
      <a href={`${CONSULTATION_URL}${d.id}`} target="_blank" rel="noreferrer" title="Ouvrir le contrat dans Shems">
        {d.numDoss || `#${d.id}`}
      </a>
    </React.Fragment>
  ));
};

const C = {
  projet:      { key: 'projet', label: 'Projet' },
  numDoss:     { key: 'numDoss', label: 'N° dossier', render: r => <Dossiers r={r} /> },
  description: { key: 'description', label: 'Désignation' },
  clients:     { key: 'clients', label: 'Client(s)' },
  commercial:  { key: 'commercial', label: 'Commercial' },
  tirePar:     { key: 'tirePar', label: 'Tiré par' },
  modeRegl:    { key: 'modeRegl', label: 'Mode' },
  banque:      { key: 'banque', label: 'Banque' },
  statut:      { key: 'statut', label: 'Statut' },
  nature:      { key: 'nature', label: 'Nature' },
  date:        { key: 'date', label: 'Date', fmt: dt },
  dateRegl:    { key: 'dateReglement', label: 'Date règlement', fmt: dt },
  part:        { key: 'part', label: 'Part %', fmt: v => (v == null ? '' : `${v} %`) },
  montant:     { key: 'montant', label: 'Montant (DH)', fmt: mt, num: true, total: true },
};

// Colonnes communes du Point du jour technique — matricule, chantier, ouvrier et citerne ouvrent leur fiche
// (r._periode = période du rapport)
const T = {
  chantier:  { key: 'chantier', label: 'Chantier', render: r => <FicheLien type="chantier" id={r.chantierId} periode={r._periode}>{r.chantier}</FicheLien> },
  matricule: { key: 'matricule', label: 'Matricule', render: r => <FicheLien type="materiel" id={r.materielId} periode={r._periode}>{r.matricule}</FicheLien> },
  type:      { key: 'type', label: 'Type' },
  ouvrier:   { key: 'nom', label: 'Ouvrier', render: r => <FicheLien type="ouvrier" id={r.ouvrierId} periode={r._periode}>{r.nom}</FicheLien> },
  citerne:   { key: 'nom', label: 'Citerne', render: r => <FicheLien type="citerne" id={r.id} periode={r._periode}>{r.nom}</FicheLien> },
};

const COLONNES = {
  ventes:  [C.projet, C.numDoss, C.description, C.clients, C.commercial, C.part, C.montant],
  liv:     [C.projet, C.numDoss, C.description, C.clients, C.commercial, C.part, C.montant],
  enc:     [C.projet, C.numDoss, C.tirePar, C.modeRegl, C.statut, C.nature, C.commercial, C.dateRegl, C.montant],
  impayes: [C.projet, C.numDoss, C.tirePar, C.modeRegl, C.banque, C.dateRegl, C.montant],
  desist:  [C.projet, C.numDoss, C.description, C.clients, C.commercial,
    { key: 'saisiPar', label: 'Saisi par' },
    { key: 'association', label: 'Type', fmt: v => (v ? "Part d'association" : 'Lot') },
    { key: 'comment', label: 'Commentaire' }, C.montant],
  ptf:     [C.dateRegl, C.projet, C.numDoss, C.tirePar, C.modeRegl, C.banque, C.montant],
  notaire: [C.projet, C.numDoss, C.description, C.clients,
    { key: 'notaire', label: 'Notaire' },
    { key: 'depot', label: 'Dépôt', fmt: dt },
    { key: 'jours', label: 'Jours', num: true },
    { key: 'prix', label: 'Prix net', fmt: mt, num: true, total: true },
    { key: 'avance', label: 'Avance', fmt: mt, num: true, total: true },
    { key: 'reliquat', label: 'Reliquat', fmt: mt, num: true, total: true }],
  impayesEnCours: [C.projet, C.numDoss, C.tirePar, C.modeRegl,
    { key: 'dateImpaye', label: 'Date impayé', fmt: dt },
    { key: 'age', label: 'Ancienneté (j)', num: true }, C.montant],
  stockDispo: [C.projet, { key: 'numBien', label: 'N° bien' }, C.description,
    { key: 'type', label: 'Type' }, { key: 'tranche', label: 'Tranche' },
    { key: 'superficie', label: 'Superficie (m²)', num: true },
    { key: 'prix', label: 'Prix catalogue', fmt: mt, num: true, total: true }],
  liveVentes: [C.date, C.projet, C.numDoss, C.description, C.clients, C.commercial, C.part, C.montant],
  liveEnc:    [C.date, C.dateRegl, C.projet, C.tirePar, C.modeRegl, C.statut, C.nature, C.commercial, C.montant],

  // ── Point du jour technique ──
  ouvriersHier: [T.chantier, T.ouvrier, { key: 'qualification', label: 'Qualification' },
    { key: 'jours', label: 'Jours', num: true, total: true }, { key: 'heures', label: 'Heures sup', num: true, total: true },
    { key: 'taux', label: 'Taux (DH/j)', fmt: mt, num: true }, { key: 'cout', label: 'Coût (DH)', fmt: mt, num: true, total: true }],
  materielsHier: [T.chantier, T.matricule, T.type, { key: 'heures', label: 'Heures', num: true, total: true },
    { key: 'litres', label: 'Gasoil (L)', fmt: mt, num: true, total: true },
    { key: 'compteurPrec', label: 'Compteur avant', num: true }, { key: 'compteurNv', label: 'Compteur après', num: true },
    { key: 'ecartMessage', label: 'Écart compteur' }],
  reparationsHier: [T.matricule, T.type, T.chantier, { key: 'refBdc', label: 'Réf. BDC' }, { key: 'designation', label: 'Désignation' },
    { key: 'fournisseur', label: 'Fournisseur' }, { key: 'qte', label: 'Qté', num: true },
    { key: 'prix', label: 'Prix', fmt: mt, num: true }, C.montant],
  gasoilPeriode: [T.matricule, T.type, T.chantier, { key: 'litres', label: 'Gasoil (L)', fmt: mt, num: true, total: true },
    { key: 'heures', label: 'Heures', num: true, total: true },
    { key: 'litresHeure', label: 'L/h', num: true, fmt: v => (v == null ? '—' : String(v).replace('.', ',')) }],
  reparationsPeriode: [T.matricule, T.type, T.chantier, { key: 'nbLignes', label: 'Lignes', num: true, total: true },
    { key: 'fournisseurs', label: 'Fournisseurs' }, C.montant],
  vidanges: [{ key: 'niveau', label: 'Niveau', fmt: v => ({ depasse: '🔴 Dépassée', critique: '🟠 Critique', alerte: '🟡 Alerte' }[v] || v) },
    T.matricule, T.type, T.chantier, { key: 'societeMateriel', label: 'Société' },
    { key: 'cycle', label: 'Depuis vidange', num: true, fmt: mt }, { key: 'interval', label: 'Intervalle', num: true, fmt: mt },
    { key: 'reste', label: 'Reste', num: true, fmt: mt }, { key: 'mode', label: 'Unité' }],
  citernes: [T.citerne, { key: 'reference', label: 'Référence' }, T.chantier,
    { key: 'stock', label: 'Stock (L)', fmt: mt, num: true, total: true }, { key: 'min', label: 'Min (L)', fmt: mt, num: true },
    { key: 'max', label: 'Max (L)', fmt: mt, num: true }],
};

// Source et filtre de chaque type de détail figé
const SOURCES = {
  ventes:          { src: 'ventes',  cols: 'ventes' },
  liv:             { src: 'liv',     cols: 'liv' },
  enc:             { src: 'enc',     cols: 'enc' },
  impayes:         { src: 'impayes', cols: 'impayes' },
  desist:          { src: 'desist',  cols: 'desist' },
  ptf7:            { src: 'ptf',     cols: 'ptf', filtre: (r, d) => r.dateReglement <= d.seuils.limite7 },
  ptf30:           { src: 'ptf',     cols: 'ptf' },
  notaire:         { src: 'notaire', cols: 'notaire' },
  notaireReliquat: { src: 'notaire', cols: 'notaire', filtre: (r, d) => r.reliquat > d.seuils.reliquatMin },
  notaireBloque:   { src: 'notaire', cols: 'notaire', filtre: (r, d) => r.depot && r.depot < d.seuils.dateBloque },
  impayesEnCours:  { src: 'impayesEnCours', cols: 'impayesEnCours' },
  stockDispo:      { src: 'stockDispo', cols: 'stockDispo' },
  // Technique (lignes rattachées à un chantier)
  ouvriersHier:       { src: 'ouvriersHier', cols: 'ouvriersHier' },
  materielsHier:      { src: 'materielsHier', cols: 'materielsHier' },
  materielsEcart:     { src: 'materielsHier', cols: 'materielsHier', filtre: r => r.ecart },
  reparationsHier:    { src: 'reparationsHier', cols: 'reparationsHier' },
  gasoilPeriode:      { src: 'gasoilPeriode', cols: 'gasoilPeriode', filtre: r => r.litres > 0 },
  heuresPeriode:      { src: 'gasoilPeriode', cols: 'gasoilPeriode', filtre: r => r.heures > 0 },
  reparationsPeriode: { src: 'reparationsPeriode', cols: 'reparationsPeriode' },
  vidanges:           { src: 'vidanges', cols: 'vidanges' },
  vidDepasse:         { src: 'vidanges', cols: 'vidanges', filtre: r => r.niveau === 'depasse' },
  vidCritique:        { src: 'vidanges', cols: 'vidanges', filtre: r => r.niveau === 'critique' },
  vidAlerte:          { src: 'vidanges', cols: 'vidanges', filtre: r => r.niveau === 'alerte' },
  citernes:           { src: 'citernes', cols: 'citernes' },
  citernesBas:        { src: 'citernes', cols: 'citernes', filtre: r => r.bas },
};

// Lignes figées d'un détail (aussi utilisé pour compter / griser les chiffres cliquables).
// ids = projets (commercial) ou chantiers (technique)
export function lignesDetail(data, detail) {
  const s = SOURCES[detail.kind];
  if (!s || !data?.details) return [];
  return (data.details[s.src] || []).filter(r =>
    (!detail.ids || detail.ids.has(r.projetId ?? r.chantierId))
    && (detail.comId == null || r.comId === detail.comId)
    && (!s.filtre || s.filtre(r, data)));
}

const DetailModal = ({ data, detail, onClose }) => {
  const [liveRows, setLiveRows] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [recherche, setRecherche] = useState('');

  useEffect(() => {
    setRecherche('');
    setLiveRows(null);
    setError('');
    if (!detail?.live) return;
    const [y, m, d] = data.date.split('-').map(Number);
    const lendemain = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
    setLoading(true);
    api.get('/decisionnel/objectif-commercial/detail', {
      params: {
        annee: data.annee, mois: data.mois, finExcl: lendemain,
        projetIds: detail.ids ? [...detail.ids].join(',') : undefined,
        ind: detail.live.ind, row: detail.live.row || 'total', periode: detail.live.periode,
      },
    })
      .then(r => setLiveRows(r.data))
      .catch(e => setError(e?.response?.status === 403
        ? "Ce détail n'est pas figé dans le rapport et nécessite l'accès au Décisionnel."
        : 'Erreur lors du chargement du détail'))
      .finally(() => setLoading(false));
  }, [detail, data]);

  const nomProjet = useMemo(() => new Map((data?.projets || []).map(p => [p.id, p.nom])), [data]);
  const nomChantier = useMemo(() => new Map((data?.chantiers || []).map(c => [c.id, c.nom])), [data]);

  const { cols, rows } = useMemo(() => {
    if (!detail) return { cols: [], rows: [] };
    if (detail.live) {
      return {
        cols: COLONNES[detail.live.ind === 'encRecouv' ? 'liveEnc' : 'liveVentes'],
        rows: (liveRows || []).map(r => ({ ...r, dossiers: r.contratId ? [{ id: r.contratId, numDoss: r.numDoss }] : [] })),
      };
    }
    const s = SOURCES[detail.kind];
    return {
      cols: COLONNES[s.cols],
      rows: lignesDetail(data, detail).map(r => ({
        ...r, projet: nomProjet.get(r.projetId) || '', chantier: nomChantier.get(r.chantierId) || '',
        _periode: data.type === 'technique' ? data.periode : null,
      })),
    };
  }, [detail, data, liveRows, nomProjet, nomChantier]);

  const filtrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => cols.some(c => String(r[c.key] ?? '').toLowerCase().includes(q)));
  }, [rows, cols, recherche]);

  const totaux = cols.filter(c => c.total).map(c => ({ ...c, v: filtrees.reduce((s, r) => s + Number(r[c.key] || 0), 0) }));

  const exporter = () => exportExcel(
    filtrees.map(r => Object.fromEntries(cols.map(c => [c.label, c.num ? Number(r[c.key] || 0) : (c.fmt ? c.fmt(r[c.key]) : r[c.key] ?? '')]))),
    `point-jour-${detail.kind || detail.live?.ind}-${data.date}`
  );

  return (
    <Modal show={!!detail} onHide={onClose} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1.05rem' }}>{detail?.titre}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {detail?.live && (
          <div className="small text-muted mb-2">ℹ️ Détail calculé à l'ouverture (données actuelles), non figé dans le rapport.</div>
        )}
        <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <input className="form-control form-control-sm" style={{ maxWidth: 260 }} placeholder="Rechercher…"
            value={recherche} onChange={e => setRecherche(e.target.value)} />
          <span className="small">
            <strong>{filtrees.length}</strong> ligne{filtrees.length > 1 ? 's' : ''}
            {totaux.map(t => <span key={t.key}> · {t.label} : <strong>{mt(t.v)}</strong></span>)}
          </span>
          <button className="btn btn-sm btn-outline-success ms-auto" disabled={!filtrees.length} onClick={exporter}>
            <FaFileExcel /> Excel
          </button>
        </div>
        {loading && <div className="text-center py-4"><Spinner animation="border" size="sm" /> Chargement…</div>}
        {error && <Alert variant="warning" className="py-2">{error}</Alert>}
        {!loading && !error && (
          <div className="table-responsive">
            <table className="table table-bordered table-sm table-striped" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr>{cols.map(c => <th key={c.key}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {filtrees.length === 0 && <tr><td colSpan={cols.length} className="text-muted">Aucune ligne</td></tr>}
                {filtrees.map((r, i) => (
                  <tr key={r.id ?? i}>
                    {cols.map(c => (
                      <td key={c.key} style={{ textAlign: c.num ? 'right' : 'left', whiteSpace: c.num ? 'nowrap' : undefined }}>
                        {c.render ? c.render(r) : c.fmt ? c.fmt(r[c.key]) : r[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DetailModal;
